import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, OnInit, inject } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { RowComponent } from "@components/row/row.component";
import { Observable, Subject, merge } from "rxjs";
import { map, scan, tap, withLatestFrom } from "rxjs/operators";
import { VKAtgardLeveransvirkesvolymDTO } from "../../models/atgard-leveransvirkesvolym-models";
import { Virkeskostnad } from "../../models/virkeskostnader/virkeskostnader-models";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { LeveransvirkesvolymMediatorService } from "../../services/leveransvirkesvolym-mediator.service";
import { AtgardVolymForm, AtgardVolymerForm } from "./atgard-volym-forms";
import { AtgardVolymRowComponent } from "./atgard-volym-row/atgard-volym-row.component";

type AtgardVolymViewModel = {
    isGetVolymButtonVisible: boolean;
};

@Component({
    selector: "rask-atgard-volym",
    templateUrl: "./atgard-volym.component.html",
    styleUrls: ["./atgard-volym.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RowComponent,
        ButtonComponent,
        FormsModule,
        ReactiveFormsModule,
        AtgardVolymRowComponent,
        AsyncPipe
    ]
})
export class AtgardVolymComponent implements OnInit {
    protected form: AtgardVolymerForm;
    protected vm$: Observable<AtgardVolymViewModel>;

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly fb = inject(FormBuilder);
    private readonly getVolymButtonClickSrc = new Subject<void>();
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly leveransvirkesvolymMediatorService = inject(LeveransvirkesvolymMediatorService);

    protected get volymer() {
        return this.form.controls.volymer;
    }

    ngOnInit(): void {
        this.form  = this.fb.group({
            volymer: this.fb.array<AtgardVolymForm>([])
        });

        const displayGetVolymFromForkalkyl$ = this.kontraktMediatorService.atgarderPreload$.pipe(
            map(atgarder => (vm: AtgardVolymViewModel) => ({
                isGetVolymButtonVisible: atgarder.length === 1
            }))
        );

        const leveransvirkesvolymer$ = this.leveransvirkesvolymMediatorService.leveransvirkesvolymer$.pipe(
            withLatestFrom(this.atgardMediatorService.atgard$),
            map(([
                volymer,
                atgard
            ]) => volymer.filter(vol => vol.virkeskopsatgardLeveransvirkeGuid === atgard.guid))
        );

        const leveransvirkesvolymerModified$ = this.leveransvirkesvolymMediatorService.leveransvirkesvolymerModified$.pipe(
            withLatestFrom(leveransvirkesvolymer$),
            map(([
                _,
                volymer
            ]) => (vm: AtgardVolymViewModel) => {
                this.volymer.clear();
                volymer.forEach(volym => {
                    this.createVolym(volym);
                });
                return vm;
            })
        );

        const getVolymButtonClick$ = this.getVolymButtonClickSrc.pipe(
            withLatestFrom(this.leveransvirkesvolymMediatorService.virkeskostnader$),
            tap(([
                _,
                virkeskostnader
            ]) => this.getVolymerFromForkalkyl(virkeskostnader)),
            map(_ => (vm: AtgardVolymViewModel) => vm)
        );

        this.vm$ = merge(
            displayGetVolymFromForkalkyl$,
            getVolymButtonClick$,
            leveransvirkesvolymerModified$
        ).pipe(
            scan((
                acc: AtgardVolymViewModel,
                mutatorFn: (value: AtgardVolymViewModel) => AtgardVolymViewModel) => mutatorFn(acc),
            {
                isGetVolymButtonVisible: false
            })
        );
    }

    onGetVolymButtonClick() {
        this.getVolymButtonClickSrc.next();
    }

    private createVolym(volym: VKAtgardLeveransvirkesvolymDTO) {
        const group: AtgardVolymForm = this.fb.group({
            atgardGuid: [volym.virkeskopsatgardLeveransvirkeGuid],
            virkeskostnadGuid: [volym.virkeskostnadGuid],
            handelssortimentnamn: [volym.handelssortimentNamn],
            mattslag: [volym.mattslag],
            kvantitet: [volym.kvantitet],
            handelssortimentId: [volym.handelssortimentId],
            handelssortimentsgruppSekvensnummer: [volym.handelssortimentsgruppSekvensnummer]
        });

        this.volymer.push(group);
    }

    private getVolymerFromForkalkyl(virkeskostnader: Virkeskostnad[]) {
        for(const group of this.volymer.controls) {
            const virkeskostnad = virkeskostnader.find(x => x.guid === group.controls.virkeskostnadGuid.value);
            group.patchValue({
                kvantitet: virkeskostnad.kvantitet
            });
        }
    }
}
